import { Color, Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import { BORDER_RADIUSES, FONT_SIZES } from 'shared/styles/variables';
import styled from 'styled-components';

import { TriggerRecurringAttributes } from 'dashboard/models/trigger';
import { WorkflowFilterFieldConfig } from 'dashboard/models/workflow-schema';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { intersperse } from 'lib/utils/array';

interface WorkflowObjectFilterConfigViewProps {
  availableConfigs: WorkflowFilterFieldConfig[];
  filters: TriggerRecurringAttributes['filter'];
}

const FilterRow = styled.div`
  display: flex;
  column-gap: ${Spacing[2]};
  padding-top: 3px;
  padding-bottom: 2px;
  align-items: center;
  font-size: ${FONT_SIZES.xs}px;
`;

const FieldDataPill = styled.span`
  background-color: ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  ${paragraphMixins.sm};
  margin: 2px;
  padding: 4px;
`;

const OperatorPill = styled.span`
  background-color: ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  box-sizing: border-box;
  ${paragraphMixins.sm};
  margin: 2px;
  padding: 4px;
`;

const ValuePill = styled.span`
  background-color: ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  box-sizing: border-box;
  ${paragraphMixins.sm};
  margin: 2px;
  padding: 4px;
`;

const StyledParagraph = styled(Paragraph)`
  padding-top: 13px;
  padding-bottom: ${Spacing[2]};
  padding-left: ${Spacing[1]};
  font-size: 13px;
  line-height: ${Spacing[4]};
`;

const WorkflowObjectFilterConfigView: VFC<WorkflowObjectFilterConfigViewProps> = ({
  availableConfigs,
  filters,
}) => {
  const getConfig = (f: TriggerRecurringAttributes['filter'][number]) => {
    return availableConfigs.find((one) => one['name'] === f['name'])?.config;
  };

  const getUnit = (f: TriggerRecurringAttributes['filter'][number]) => {
    return availableConfigs
      .find((one) => one['name'] === f['name'])
      ?.config.units?.find((unit) => unit.value === f.unit);
  };

  const getOperator = (f: TriggerRecurringAttributes['filter'][number]) => {
    return availableConfigs
      .find((one) => one['name'] === f['name'])
      ?.config.operators.find((op) => op.value === f.operator);
  };

  const filterNodes = filters
    .filter((f) => {
      const config = getConfig(f);

      return (
        f['name'] &&
        f['operator'] &&
        f['value'] &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ((config?.units?.length > 0 && f['unit']) || !config?.units || config?.units?.length < 1)
      );
    })
    .map((f, index) => {
      const operator = getOperator(f);

      const unit = getUnit(f);

      return (
        <FilterRow key={`${f.name}${index}`}>
          <FieldDataPill>{f.name}</FieldDataPill>
          <OperatorPill>{operator?.['label']}</OperatorPill>
          <ValuePill>{Array.isArray(f.value) ? `[${f.value.join(',')}]` : f.value}</ValuePill>
          {f.unit && (
            <ValuePill>
              <span>{unit?.['label'] ? unit['label'] : null}</span>
            </ValuePill>
          )}
        </FilterRow>
      );
    });
  return (
    <>
      {intersperse(filterNodes, (i) => (
        <StyledParagraph key={`separator-${i}`}>{' And '}</StyledParagraph>
      ))}
    </>
  );
};

export default WorkflowObjectFilterConfigView;
